import React from "react";
import { MARKETING_PANEL } from "@/lib/marketingPanel";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden pt-10 md:pt-16 pb-16 md:pb-24"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="hero-blob" aria-hidden="true" />
      <div
        className="absolute -left-40 -bottom-40 w-[420px] h-[420px] rounded-full bg-blue-500/10 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* Left */}
        <div className="reveal">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide mb-6"
          >
            <Sparkles size={14} />
            B2B Commodity Trading
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-slate-900"
          >
            YOU NEED THE{" "}
            <span className="text-blue-600">BEST COMPETITIVE</span>{" "}
            <span className="relative inline-block">
              PRICES
              <span className="absolute -bottom-1 left-0 right-0 h-2 bg-red-500/80 rounded-full -z-10" />
            </span>
          </h1>

          <p
            data-testid="hero-description"
            className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            Run online tenders with a global network of verified manufacturers
            and suppliers. Source commodities like oil, wheat, pasta, and more —
            at the most competitive prices, in a transparent, secure process.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={MARKETING_PANEL.createBid}
              data-testid="hero-create-bid-btn"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-blue-600 text-white font-bold text-base shadow-lg shadow-blue-600/25 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-600/30 btn-press cta-pulse"
            >
              Create a Bid
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href="#process"
              data-testid="hero-learn-more-btn"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-white text-slate-800 font-bold text-base border border-slate-200 hover:border-blue-300 hover:text-blue-700 btn-press"
            >
              How it works
            </a>
          </div>

          {/* Trust bar */}
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-slate-500 font-medium">
            <div className="flex -space-x-2">
              {[
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=faces",
                "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=faces",
                "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=faces",
                "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=faces",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <span>
              <strong className="text-slate-800">5,000+</strong> buyers &
              suppliers trust CommodityBid
            </span>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative reveal">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-100 bg-white aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&h=1400&fit=crop"
              alt="Diverse business professionals"
              className="w-full h-full object-cover"
            />
            {/* Floating stat card */}
            <div className="absolute left-4 bottom-4 md:left-6 md:bottom-6 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-xl border border-slate-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  Live auctions
                </p>
                <p className="text-base font-extrabold text-slate-900">
                  124 active now
                </p>
              </div>
            </div>
            <div className="absolute right-4 top-4 md:right-6 md:top-6 bg-red-500 text-white rounded-2xl px-4 py-3 shadow-xl rotate-3">
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">
                Saved
              </p>
              <p className="text-lg font-extrabold">-27% avg.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
